'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import clsx from 'clsx';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import { useIsClient } from '@/hooks';

/**
 * Props for `HeroCtaImageFeature`.
 */
export type HeroCtaImageFeatureProps =
  SliceComponentProps<Content.HeroCtaImageFeatureSlice>;

/**
 * Component for "HeroCtaImageFeature" Slices.
 * A footer CTA section with headline, description, CTA button, social links, and feature image.
 */
const HeroCtaImageFeature: FC<HeroCtaImageFeatureProps> = ({ slice }) => {
  const { headline, description, primary_cta, social_links, feature_image } =
    slice.primary;
  const isPlusAboutSection = slice.variation === 'plusAboutSection';
  const secondHeadline = isPlusAboutSection
    ? slice.primary.second_headline
    : null;
  const secondDescription = isPlusAboutSection
    ? slice.primary.second_description
    : null;
  const secondSocialLinks = isPlusAboutSection
    ? slice.primary.second_social_links
    : [];

  const contentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const secondSectionRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const contentEl = contentRef.current;
    const imageEl = imageRef.current;
    const secondSectionEl = secondSectionRef.current;

    if (contentEl) {
      contentEl.style.transitionDelay = '0ms';
      observer.observe(contentEl);
    }

    if (imageEl) {
      imageEl.style.transitionDelay = '200ms';
      observer.observe(imageEl);
    }

    if (secondSectionEl) {
      secondSectionEl.style.transitionDelay = isPlusAboutSection
        ? '300ms'
        : '0ms';
      observer.observe(secondSectionEl);
    }

    return () => {
      if (contentEl) {
        observer.unobserve(contentEl);
      }
      if (imageEl) {
        observer.unobserve(imageEl);
      }
      if (secondSectionEl) {
        observer.unobserve(secondSectionEl);
      }
    };
  }, [isClient, isPlusAboutSection]);

  const renderSocialLinks = (
    links:
      | Content.HeroCtaImageFeatureSliceDefaultPrimarySocialLinksItem[]
      | Content.HeroCtaImageFeatureSlicePlusAboutSectionPrimarySocialLinksItem[]
      | Content.HeroCtaImageFeatureSlicePlusAboutSectionPrimarySecondSocialLinksItem[],
    iconClassName = 'w-6 h-6 object-contain',
  ) => {
    if (!links || links.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-7 items-center justify-center">
        {links.map((item, index) => (
          <PrismicNextLink
            key={index}
            field={item.link}
            className="rounded-full p-1 hover:opacity-80 transition-opacity duration-200"
          >
            {isFilled.image(item.icon) ? (
              <PrismicNextImage field={item.icon} className={iconClassName} />
            ) : (
              <div className="w-6 h-6" />
            )}
          </PrismicNextLink>
        ))}
      </div>
    );
  };

  return (
    <footer
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={clsx(isPlusAboutSection ? 'bg-black' : 'bg-[#002726]')}
    >
      {/* Main CTA Section */}
      <div
        className={clsx(
          'px-5 md:px-16',
          isPlusAboutSection
            ? 'pt-16 md:pt-20 pb-10 md:pb-12'
            : 'py-16 md:py-20 lg:py-28',
        )}
      >
        <div
          className={clsx(
            'max-w-[1040px] mx-auto items-center',
            isPlusAboutSection
              ? 'flex justify-center'
              : 'flex flex-col lg:flex-row gap-10 lg:gap-[60px]',
          )}
        >
          {/* Left Content */}
          <div
            ref={contentRef}
            className={clsx(
              'opacity-0 translate-y-8 transition-all duration-700 ease-out',
              isPlusAboutSection
                ? 'relative overflow-hidden rounded-[32px] w-full max-w-[1040px] min-h-[360px] md:min-h-[460px] px-6 py-12 md:px-16 md:py-16 flex items-center justify-center text-center'
                : 'w-full lg:w-[520px] shrink-0 flex flex-col gap-6',
            )}
          >
            {isPlusAboutSection && (
              <>
                {isFilled.image(feature_image) && (
                  <div className="absolute inset-0">
                    <PrismicNextImage
                      field={feature_image}
                      className="w-full h-full object-cover object-center"
                      priority
                    />
                  </div>
                )}
                <div className="absolute inset-0" />
              </>
            )}

            <div
              className={clsx(
                'relative z-10 flex flex-col gap-6',
                isPlusAboutSection ? 'items-center' : 'w-full',
              )}
            >
              {/* Headline with highlight support */}
              {isFilled.richText(headline) && (
                <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white">
                  <HighlightedText
                    text={asText(headline) || ''}
                    highlightClassName="text-[#05f273]"
                  />
                </h2>
              )}

              {/* Description */}
              {isFilled.richText(description) && (
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-lg font-normal leading-[1.5] text-white">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}

              {/* Primary CTA Button - Scrolls to top of page */}
              {primary_cta?.text && (
                <div className="flex flex-col items-center gap-6">
                  <button
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className={clsx(
                      'cursor-pointer',
                      isPlusAboutSection
                        ? 'primary-button'
                        : 'inline-flex items-center justify-center bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] text-white font-bold text-base leading-[1.5] px-6 py-3 rounded-full hover:bg-[rgba(234,234,234,0.4)] transition-colors duration-200',
                    )}
                  >
                    {primary_cta.text}
                  </button>

                  {/* Social Links - Centered below button */}
                  {renderSocialLinks(social_links)}
                </div>
              )}
            </div>
          </div>

          {/* Right Feature Image */}
          {!isPlusAboutSection && isFilled.image(feature_image) && (
            <div
              ref={imageRef}
              className="flex-1 relative w-full lg:w-auto h-[300px] sm:h-[400px] lg:h-[540px] opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              <PrismicNextImage
                field={feature_image}
                className="w-full h-full object-contain object-center"
                priority
              />
            </div>
          )}
        </div>
      </div>

      {isPlusAboutSection && (
        <div className="px-5 md:px-16 pb-16 md:pb-20">
          <div
            ref={secondSectionRef}
            className="max-w-[760px] mx-auto flex flex-col items-center text-center gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {isFilled.richText(secondHeadline) && (
              <div className="text-white">
                <PrismicRichText
                  field={secondHeadline}
                  components={{
                    heading1: ({ children }) => (
                      <h2 className="text-[28px] md:text-[38px] font-medium leading-[1.2]">
                        {children}
                      </h2>
                    ),
                    heading2: ({ children }) => (
                      <h2 className="text-[28px] md:text-[38px] font-medium leading-[1.2]">
                        {children}
                      </h2>
                    ),
                    paragraph: ({ children }) => (
                      <p className="text-[28px] md:text-[38px] font-medium leading-[1.2]">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            )}

            {isFilled.richText(secondDescription) && (
              <PrismicRichText
                field={secondDescription}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg font-normal leading-[1.5] text-white/80">
                      {children}
                    </p>
                  ),
                }}
              />
            )}

            {renderSocialLinks(secondSocialLinks)}
          </div>
        </div>
      )}

      {/* Copyright Section */}
      <div className="border-t border-b border-[#666] px-4 md:px-8 lg:px-[200px] py-6">
        <p className="text-lg font-normal leading-[1.5] text-[#666] text-center">
          © {new Date().getFullYear()} Devsu All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default HeroCtaImageFeature;
